<template>
    <div class="relative">
        <button
            ref="buttonRef"
            class="flex h-12 items-center justify-between rounded-lg bg-white text-base font-medium text-gray-500 hover:bg-gray-200 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            :class="
                props.compact ? 'w-full justify-center px-0' : 'w-full px-4'
            "
            @click="toggle"
        >
            <span
                class="flex items-center"
                :class="props.compact ? '' : 'gap-7'"
            >
                <span
                    class="inline-flex h-5 w-5 items-center justify-center text-[16px] leading-none"
                    >{{ getCurrentLanguageFlag() }}</span
                >

                <span v-if="!props.compact" class="leading-none">
                    {{ getCurrentLanguageName() }}
                </span>
            </span>

            <svg
                v-if="!props.compact"
                class="h-4 w-4 shrink-0 transition-transform"
                :class="{ 'rotate-90': isOpen }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                />
            </svg>
        </button>

        <teleport to="body">
            <div
                v-if="isOpen"
                v-click-away="closeDropdown"
                class="fixed z-[9999] w-[200px] rounded-lg border border-[#E5E7EB] bg-white py-1 shadow-lg"
                :style="dropdownStyle"
            >
                <a
                    v-for="language in availableLanguages"
                    :key="language.code"
                    :href="`/language/${language.code}`"
                    class="flex items-center gap-3 px-4 py-2 text-sm text-gray-900 hover:bg-gray-100"
                    @click="isOpen = false"
                >
                    <span
                        class="inline-flex h-5 w-5 items-center justify-center text-[16px] leading-none"
                        >{{ language.flag }}</span
                    >
                    <span class="leading-none">{{ language.name }}</span>
                </a>
            </div>
        </teleport>
    </div>
</template>

<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { usePage } from "@inertiajs/vue3";
import { useTranslations } from "@/composables/useTranslations.ts";

const props = defineProps({
    compact: { type: Boolean, default: false },
    sidebarRight: { type: Number, default: 0 },
});

const isOpen = ref(false);

const { t } = useTranslations();

const availableLanguages = [
    { code: "en", name: t("vue.english_lang"), flag: "🇺🇸" },
    { code: "cs", name: t("vue.czech_lang"), flag: "🇨🇿" },
];

const page = usePage();
const currentLocale = computed(() => page.props.locale || "en");

const getCurrentLanguageFlag = () => {
    const language = availableLanguages.find(
        (lang) => lang.code === currentLocale.value,
    );
    return language?.flag || "🇺🇸";
};

const getCurrentLanguageName = () => {
    const language = availableLanguages.find(
        (lang) => lang.code === currentLocale.value,
    );
    return language?.name || t("vue.english_lang");
};

const closeDropdown = () => {
    isOpen.value = false;
};

const buttonRef = ref<HTMLElement | null>(null);
const dropdownPos = ref({ top: 0, left: 0 });

const dropdownStyle = computed(() => ({
    top: `${dropdownPos.value.top}px`,
    left: `${dropdownPos.value.left}px`,
}));

const updateDropdownPos = () => {
    const el = buttonRef.value;
    if (!el) return;

    const r = el.getBoundingClientRect();

    dropdownPos.value = {
        top: Math.round(r.top),
        left: Math.round((props.sidebarRight || r.right) + 5),
    };
};

const toggle = async () => {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        await nextTick();
        updateDropdownPos();
    }
};

const onReposition = () => {
    if (isOpen.value) updateDropdownPos();
};

onMounted(() => {
    window.addEventListener("resize", onReposition, { passive: true });
    window.addEventListener("scroll", onReposition, { passive: true });
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", onReposition);
    window.removeEventListener("scroll", onReposition);
});

watch(
    () => props.compact,
    () => {
        isOpen.value = false;
    },
);
</script>
